import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Paper } from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import { useModulePermissions } from '../hooks/useModulePermissions';
import { useAuth } from '../contexts/AuthContext';
import { useSelector } from 'react-redux';
import { selectPermissionsLoading } from '../app/store/slices/authSlice';

/**
 * ModuleProtectedRoute Component
 * Protects routes based on module-level permissions from Access Management
 * 
 * @param {ReactNode} children - Component to render if user has access
 * @param {string} moduleName - Module name (e.g., 'Invoices', 'Customers')
 * @param {string} requiredPermission - Permission level: 'view', 'edit', 'create', 'delete'
 * @param {string} redirectTo - Where to redirect if access denied (default: '/unauthorized')
 */
const ModuleProtectedRoute = ({ 
  children, 
  moduleName, 
  requiredPermission = 'view',
  redirectTo = '/unauthorized',
  fallback = null,
}) => {
  const { checkModuleAccess } = useModulePermissions();
  const { loading: authLoading, isAuthenticated, isSuperAdmin, isCompanyAdmin } = useAuth();
  const permissionsLoadingFromRedux = useSelector(selectPermissionsLoading);

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="body1" color="text.secondary">
          Checking authentication...
        </Typography>
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: window.location.pathname }} replace />;
  }

  // ✅ For non-admin users, wait for permissions to finish loading
  // Admin users (SuperAdmin/CompanyAdmin) bypass this check as they have full access
  if (!isSuperAdmin && !isCompanyAdmin && permissionsLoadingFromRedux) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="body1" color="text.secondary">
          Loading permissions...
        </Typography>
      </Box>
    );
  }

  // Check if user has required module permission
  const hasAccess = checkModuleAccess(moduleName, requiredPermission);

  if (!hasAccess) {
    // If custom fallback provided, show it
    if (fallback) {
      return fallback;
    }

    // Otherwise redirect to unauthorized page
    return <Navigate to={redirectTo} replace />;
  }

  // User has access, render the protected component
  return children;
};

/**
 * UnauthorizedPage Component
 * Default page shown when user doesn't have module access
 */
export const UnauthorizedPage = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        p: 3,
        bgcolor: '#f5f5f5',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 500,
          width: '100%',
          textAlign: 'center',
        }}
      >
        <LockOutlined sx={{ fontSize: 80, color: '#f44336', mb: 2 }} />
        <Typography variant="h4" fontWeight={600} gutterBottom>
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          You don't have permission to access this page. Please contact your administrator 
          if you believe this is an error.
        </Typography>
        <Typography variant="caption" color="text.disabled">
          If you need access to this module, ask your Company Admin to grant you permissions 
          in the Access Management section.
        </Typography>
      </Paper>
    </Box>
  );
};

export default ModuleProtectedRoute;
